import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { authApi } from '../../api/auth.api';
import { errorMessage } from '@/shared/api/problem';

const PASSWORD_MIN = 10;
import { Button } from '@/shared/ui/atoms/Button';
import { PasswordInput } from '@/shared/ui/molecules/PasswordInput';
import { FormField } from '@/shared/ui/molecules/FormField';
import '../AuthForm.styl';

type Props = { token: string | null };

export function ResetForm({ token }: Props) {
  const { t } = useTranslation(undefined, { keyPrefix: 'auth.reset' });
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const reset = useMutation({
    mutationFn: () => authApi.reset(token ?? '', password),
    onSuccess: () => setDone(true),
    onError: (err) => setError(errorMessage(err, t('missingToken')))
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!token) return setError(t('missingToken'));
    if (password.length < PASSWORD_MIN) return setError(t('tooShort'));
    if (password !== confirm) return setError(t('mismatch'));
    reset.mutate();
  };

  return (
    <form className="f-auth" onSubmit={submit} noValidate>
      <h1 className="f-auth__title">{t('title')}</h1>
      <p className="f-auth__lead">{t('lead')}</p>
      <div className="f-auth__fields">
        <FormField label={t('password')} htmlFor="reset-pwd">
          <PasswordInput id="reset-pwd" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} disabled={done} />
        </FormField>
        <FormField label={t('confirm')} htmlFor="reset-confirm">
          <PasswordInput id="reset-confirm" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} disabled={done} />
        </FormField>
      </div>
      {error ? (
        <div className="f-auth__alert" role="alert">
          {error}
        </div>
      ) : null}
      {done ? (
        <div className="f-auth__status" role="status">
          {t('done')}
        </div>
      ) : (
        <Button type="submit" block className="f-auth__submit" loading={reset.isPending}>
          {t('submit')}
        </Button>
      )}
      <div className="f-auth__foot">
        <Link className="f-auth__link" to="/login">
          {t('back')}
        </Link>
      </div>
    </form>
  );
}
